import { Input } from '@/components/ui/input'
import { useContext, useRef, useState } from 'react'
import { FormContext } from '../FormProvider'
import { FormFieldProps, FormKeywordsType } from './type'
import { Badge } from '@/components/ui/badge'
import { ControllerRenderProps, FieldValues } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { CheckIcon, XIcon } from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { useOffsetInfiniteList } from '@/@diffrag/hooks/useOffsetInfiniteList'
import classNames from 'classnames'

export const FormKeywordsField = ({
  data,
  field,
}: FormFieldProps<FormKeywordsType>) => {
  const context = useContext(FormContext)
  const textInput = useRef<HTMLInputElement>(null)
  const [text, setText] = useState('')

  const [isOpenCommand, setIsOpenCommand] = useState(false)
  const [searchText, setSearchText] = useState('')

  const { rows, listBottom } = useOffsetInfiniteList({
    id: `keywords-field-search-${field.name}`,
    fn: data.searchApiFn,
    params: {
      amount: 30,
      page: 1,
      searchText,
    },
    enabled: isOpenCommand && !!data.searchApiFn,
  })

  const onAdd = (
    field: ControllerRenderProps<FieldValues, string>,
    value: { id: any; title: string },
  ) => {
    if (isSelected(value)) {
      onRemove(field, value)
      return
    }

    field.onChange([...(field.value || []), value])
    setText('')
  }

  const onRemove = (
    field: ControllerRenderProps<FieldValues, string>,
    value: { id: any; title: string },
  ) => {
    field.onChange([
      ...(field.value || []).filter((k: { id: any }) => k.id !== value.id),
    ])
  }

  const isSelected = (keyword: { id: any; title: string }) => {
    return field.value?.some((k: { id: any }) => k.id === keyword.id)
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1 rounded border bg-white p-2">
        {!field.value?.length ? (
          <span className="text-[13px] text-gray-600">내용이 없습니다.</span>
        ) : (
          field.value?.map((keyword: { id: any; title: string }) => {
            return (
              <Badge
                key={keyword.id}
                variant="outline"
                className="bg-white px-2 py-1"
              >
                <span className="mr-1">{keyword.title}</span>
                <XIcon
                  size={14}
                  onClick={() => onRemove(field, keyword)}
                  className="cursor-pointer"
                />
              </Badge>
            )
          })
        )}
      </div>
      {!data.readOnly && (
        <div className="flex  gap-1">
          <Popover open={isOpenCommand} onOpenChange={setIsOpenCommand}>
            <PopoverTrigger>
              <Input
                ref={textInput}
                value={text}
                onChange={(e) => setText(e.target.value)}
                className="w-[200px] disabled:opacity-100"
                placeholder={data.label}
                onKeyDown={(e) => {
                  if (!e.nativeEvent.isComposing && e.key === 'Enter') {
                    e.preventDefault()
                    // onAdd(field, {  })
                  }
                }}
              />
            </PopoverTrigger>
            <PopoverContent className="p-0">
              <Command>
                <CommandInput
                  placeholder="키워드 검색"
                  value={searchText}
                  onValueChange={setSearchText}
                  className="h-9 text-[13px]"
                />
                <CommandList className="h-[160px]">
                  <CommandEmpty className="p-4 text-center text-sm text-neutral-500">
                    검색 결과가 없습니다.
                  </CommandEmpty>
                  {rows.map((row) => (
                    <CommandItem
                      onSelect={() => onAdd(field, row)}
                      key={row.id}
                      className={classNames(
                        'flex h-8 items-center justify-between text-[13px]',
                        {
                          'bg-yellow-100': isSelected(row),
                        },
                      )}
                    >
                      <span>{row.title}</span>

                      {isSelected(row) && <CheckIcon size={14} />}
                    </CommandItem>
                  ))}

                  <div ref={listBottom} />
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-8 text-[13px]"
          >
            + 새로만들기
          </Button>
        </div>
      )}
    </div>
  )
}
